import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from "react";
import { canCustomisePagesAndDashboards, getComponents, normalizeComponentId } from "@utils";
import { UXPContext } from "@core/context/Context";
import { searchComponents } from "@core/services/services";
import ErrorState from "@helper/ErrorState";
import ButtonComponent from '@widget/buttons/ButtonComponent';
const APP_SCRIPT = /^\/Resources\/([^/]+)\//i;
const MODULE_SCRIPT = /\/(uxp-module\.js|api\/UXP\/module)\?id=/i;
/**
 * The script URL is the only reliable signal of where a module comes from — the id's
 * shape (GUID vs bundle id) is a convention, not a contract.
 */
export function describeModule(context, moduleId) {
    var _a;
    const scripts = (moduleId && ((_a = context === null || context === void 0 ? void 0 : context.scriptFiles) === null || _a === void 0 ? void 0 : _a[moduleId])) || [];
    const appScript = scripts.map(s => s.match(APP_SCRIPT)).find(m => m);
    if (appScript)
        return { moduleId, kind: 'app', appId: appScript[1], scripts };
    if (scripts.some(s => MODULE_SCRIPT.test(s)))
        return { moduleId, kind: 'module', scripts };
    return { moduleId, kind: 'unknown', scripts };
}
const NOT_FOUND_CAUSE_MESSAGE = {
    'no-ids': 'uxp-core.text.view-not-found-cause-no-ids',
    'no-page': 'uxp-core.text.view-not-found-cause-no-page',
    'bundle-missing': 'uxp-core.text.view-not-found-cause-bundle-missing',
    'not-registered': 'uxp-core.text.view-not-found-cause-not-registered',
    'load-failed': 'uxp-core.text.view-not-found-cause-load-failed',
};
const NOT_FOUND_CAUSE_HINTS = {
    'no-ids': [
        'uxp-core.text.view-not-found-hint-page-id',
        'uxp-core.text.view-not-found-hint-widget-component',
    ],
    'no-page': [
        'uxp-core.text.view-not-found-hint-choose-page',
        'uxp-core.text.view-not-found-hint-change-link-type',
    ],
    'bundle-missing': [
        'uxp-core.text.view-not-found-hint-bundle-id',
        'uxp-core.text.view-not-found-hint-app-enabled',
        'uxp-core.text.view-not-found-hint-resync',
    ],
    'not-registered': [
        'uxp-core.text.view-not-found-hint-registered',
        'uxp-core.text.view-not-found-hint-console',
        'uxp-core.text.view-not-found-hint-rebuild',
    ],
    'load-failed': [
        'uxp-core.text.view-not-found-hint-network',
        'uxp-core.text.view-not-found-hint-deployed',
        'uxp-core.text.view-not-found-hint-clear-cache',
    ],
};
let serverComponentsPromise = null;
function fetchServerComponents(context) {
    // Memoised per page load. Fail-open: any error resolves to null, which keeps the
    // plain wording rather than asserting something the check could not establish.
    // Same call as the widget drawer (see searchComponents for why it is not a batch call).
    if (!serverComponentsPromise) {
        serverComponentsPromise = searchComponents(context)
            .then(r => (Array.isArray(r === null || r === void 0 ? void 0 : r.data) ? r.data : null))
            .catch(() => null);
    }
    return serverComponentsPromise;
}
/** Admin-only, on demand: does the server know this component at all, and as what? */
function useServerExistence(context, id) {
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!id) {
            setState(null);
            return;
        }
        let cancelled = false;
        setState('pending');
        fetchServerComponents(context).then(list => {
            if (cancelled)
                return;
            if (!list) {
                setState('unavailable');
                return;
            }
            const wanted = normalizeComponentId(id);
            const hit = list.find(c => String((c === null || c === void 0 ? void 0 : c.normalizedId) || '').toLowerCase() === wanted);
            setState(!hit ? 'unknown'
                : hit.installed === false ? 'marketplace-only'
                    : hit.sourceUrl ? 'widget-module'
                        : 'app-component');
        });
        return () => { cancelled = true; };
    }, [context, id]);
    return state;
}
function resolveCopy(diagnostic, server) {
    const { cause, kind } = diagnostic;
    if (cause === 'bundle-missing') {
        switch (server) {
            case 'widget-module':
                return {
                    message: 'uxp-core.text.view-not-found-cause-stale-page',
                    hints: ['uxp-core.text.view-not-found-hint-reload', 'uxp-core.text.view-not-found-hint-clear-modules-cache'],
                    manageBundles: true,
                };
            case 'app-component':
                return {
                    message: 'uxp-core.text.view-not-found-cause-app-not-delivered',
                    hints: NOT_FOUND_CAUSE_HINTS['bundle-missing'],
                    manageBundles: false,
                };
            case 'marketplace-only':
                return {
                    message: 'uxp-core.text.view-not-found-cause-marketplace-only',
                    hints: ['uxp-core.text.view-not-found-hint-install-marketplace'],
                    manageBundles: false,
                };
            case 'unknown':
                return {
                    message: 'uxp-core.text.view-not-found-cause-unknown-to-server',
                    hints: ['uxp-core.text.view-not-found-hint-uploaded-not-registered', 'uxp-core.text.view-not-found-hint-app-config'],
                    manageBundles: true,
                };
            default:
                return { message: NOT_FOUND_CAUSE_MESSAGE[cause], hints: NOT_FOUND_CAUSE_HINTS[cause], manageBundles: false };
        }
    }
    if (cause === 'not-registered' && kind === 'module') {
        return {
            message: NOT_FOUND_CAUSE_MESSAGE[cause],
            hints: ['uxp-core.text.view-not-found-hint-uploaded-missing-component'],
            manageBundles: true,
        };
    }
    return {
        message: NOT_FOUND_CAUSE_MESSAGE[cause],
        hints: NOT_FOUND_CAUSE_HINTS[cause],
        manageBundles: cause === 'load-failed' && kind === 'module',
    };
}
const REGISTERED_LIMIT = 12;
/**
 * The shared "unable to find the view" block. Everyone sees the title and the friendly
 * description; the diagnosis below it is gated on the customise role.
 */
const ViewNotFound = ({ diagnostic, notFoundMessage }) => {
    var _a;
    const context = useContext(UXPContext);
    const showDiagnostics = !!diagnostic && canCustomisePagesAndDashboards(context);
    const server = useServerExistence(context, showDiagnostics && diagnostic.cause === 'bundle-missing' ? diagnostic.id : null);
    const registered = useMemo(() => {
        var _a;
        const prefix = (_a = diagnostic === null || diagnostic === void 0 ? void 0 : diagnostic.moduleId) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        return prefix ? getComponents().filter(c => { var _a; return ((_a = c.normalizedId) === null || _a === void 0 ? void 0 : _a.split('/')[0]) === prefix; }) : [];
    }, [diagnostic === null || diagnostic === void 0 ? void 0 : diagnostic.moduleId]);
    // A caller-supplied message renders as given.
    if (notFoundMessage)
        return _jsx(_Fragment, { children: notFoundMessage });
    const copy = diagnostic ? resolveCopy(diagnostic, server) : null;
    return (_jsx(ErrorState, { variant: "warning", title: context.$L('uxp-core.text.unable-to-find-the-view'), 
        // the one-line form keeps the plain sentence visible; the details open on click
        summary: context.$L('uxp-core.text.view-not-found-description'), description: _jsxs(_Fragment, { children: [_jsx("p", { children: context.$L('uxp-core.text.view-not-found-description') }), showDiagnostics && (_jsxs("div", { className: "uxpcore_componentrenderer__not-found-diagnostics", children: [_jsx("p", { children: context.$L(copy.message) }), _jsx("code", { className: "uxpcore_componentrenderer__not-found-id", children: diagnostic.id }), diagnostic.kind === 'app' && diagnostic.appId && (_jsx("p", { children: context.$L('uxp-core.text.view-not-found-module-app', { appId: diagnostic.appId }) })), diagnostic.kind === 'module' && _jsx("p", { children: context.$L('uxp-core.text.view-not-found-module-widget') }), server === 'pending' && _jsx("p", { children: context.$L('uxp-core.text.view-not-found-checking') }), diagnostic.cause === 'not-registered' && (_jsxs(_Fragment, { children: [_jsx("p", { className: "uxpcore_componentrenderer__not-found-hints-title", children: context.$L('uxp-core.text.view-not-found-registered-title') }), registered.length === 0
                                    ? _jsx("p", { children: context.$L('uxp-core.text.view-not-found-registered-none') })
                                    : (_jsxs("ul", { className: "uxpcore_componentrenderer__not-found-registered", children: [registered.slice(0, REGISTERED_LIMIT).map(c => (_jsxs("li", { children: [_jsx("span", { children: c.name || c.id }), _jsx("code", { className: "uxpcore_componentrenderer__not-found-id", children: c.normalizedId }), _jsx("span", { className: "uxpcore_componentrenderer__not-found-modes", children: (c.modes || []).join(', ') })] }, c.normalizedId))), registered.length > REGISTERED_LIMIT && (_jsx("li", { children: context.$L('uxp-core.text.view-not-found-registered-more', { count: registered.length - REGISTERED_LIMIT }) }))] }))] })), diagnostic.cause === 'load-failed' && !!((_a = diagnostic.scripts) === null || _a === void 0 ? void 0 : _a.length) && (_jsxs(_Fragment, { children: [_jsx("p", { className: "uxpcore_componentrenderer__not-found-hints-title", children: context.$L('uxp-core.text.view-not-found-scripts-title') }), diagnostic.scripts.map(s => (_jsx("code", { className: "uxpcore_componentrenderer__not-found-id", children: s }, s)))] })), _jsx("p", { className: "uxpcore_componentrenderer__not-found-hints-title", children: context.$L('uxp-core.text.view-not-found-what-to-check') }), _jsx("ul", { className: "uxpcore_componentrenderer__not-found-hints", children: copy.hints.map(key => _jsx("li", { children: context.$L(key) }, key)) })] }))] }), action: showDiagnostics && copy.manageBundles
            ? _jsx(ButtonComponent, { size: "small", title: context.$L('uxp-core.text.view-not-found-manage-bundles'), leftIcon: "fas cubes", 
                // useRouterContext throws outside a Router (this block also renders in the
                // showcase), so navigate the browser rather than depend on one being mounted.
                onClick: () => { window.location.href = '/view/uxp/settings'; } })
            : undefined }));
};
export default ViewNotFound;
